import { extractSwVersionHash } from "components/UpdateManager/UpdateManager.utils";
import * as React from "react";
import UpdateNotification from "./UpdateNotification";

interface IProps {
  children: React.ReactNode;
}

interface IState {
  updateCheckTimerId?: number;
  checkUserIdleStateTimerId?: number;
  loadNewAppVersionTimerId?: number;
  updateAvailable: boolean;
  updateSourceSW?: ServiceWorker;
  currentVersionHash?: string;
  lastUserInteraction: number;
  autoUpdateStarted: boolean;
}

// Update polling settings
const UPDATE_CHECK_POLLING_INTERVAL = 300000; // Check backend for new updates every 5 mins
const DELAY_BEFORE_MANUAL_RESTART = 500;
// Auto-update on idle settings
const IDLE_CHECK_INTERVAL = 120000; // Check if the tablet is idle every 2 mins
const AUTO_UPDATE_IDLE_DELAY = 900000; // Auto-update if tablet has been idle for 15 mins
const AUTO_UPDATE_NOTIFICATION_DELAY = 3000; // Show auto update in progress message for 3s before restart

export const UpdateManagerContext = React.createContext<undefined>(undefined);

class UpdateManager extends React.Component<IProps, IState> {
  public constructor(props: IProps) {
    super(props);
    this.state = {
      updateAvailable: false,
      lastUserInteraction: Date.now(),
      autoUpdateStarted: false
    };

    this.handleUpdateFoundSWEvent = this.handleUpdateFoundSWEvent.bind(this);
    this.handleUserInteraction = this.handleUserInteraction.bind(this);
  }

  public componentWillUnmount() {
    // Start listening for service worker update found events
    document.removeEventListener(
      "swUpdateFound",
      this.handleUpdateFoundSWEvent
    );

    // Remove event listeners
    window.removeEventListener("touchend", this.handleUserInteraction, true);
    window.removeEventListener("mouseup", this.handleUserInteraction, true);

    // Remove intervals
    window.clearInterval(this.state.checkUserIdleStateTimerId);
    window.clearInterval(this.state.updateCheckTimerId);
    window.clearInterval(this.state.loadNewAppVersionTimerId);
  }

  public render() {
    return (
      <>
        {this.state.updateAvailable && (
          <UpdateNotification
            onStartNewVersion={this.loadNewAppVersion}
            autoUpdateStarted={this.state.autoUpdateStarted}
          />
        )}
        <UpdateManagerContext.Provider value={undefined}>
          {this.props.children}
        </UpdateManagerContext.Provider>
      </>
    );
  }

  public async componentDidMount() {
    // Start listening for service worker update found events

    document.addEventListener("swUpdateFound", this.handleUpdateFoundSWEvent);

    // Start tracking user interactions (Touch, Click)
    window.addEventListener("touchend", this.handleUserInteraction, true);
    window.addEventListener("mouseup", this.handleUserInteraction, true);

    // Start Idle detection timer
    const checkUserIdleStateTimerId = window.setInterval(
      () => this.checkUserIdleState(),
      IDLE_CHECK_INTERVAL
    );
    // Set Idle detection timer Id
    this.setState({
      checkUserIdleStateTimerId
    });

    // Capture current version so we can do additional polling version check
    this.captureCurrentVersion().then(
      (currentVersionHash: string | undefined) => {
        if (currentVersionHash) {
          // Start version check polling
          const updateCheckTimerId = window.setInterval(
            () => this.checkForUpdates(),
            UPDATE_CHECK_POLLING_INTERVAL
          );
          this.setState({
            currentVersionHash,
            updateCheckTimerId
          });
          console.info("Captured current SW version as: ", currentVersionHash);
        }
      }
    );
  }

  /**
   * Fetch and extract synthetic version id for current Service Worker (Prefer Cache)
   */
  private captureCurrentVersion(): Promise<string | undefined> {
    console.log("Capturing current SW version");
    return new Promise<string | undefined>(resolve => {
      fetch("/service-worker.js", { cache: "force-cache" })
        .then(response => {
          if (response.ok) {
            return response.text();
          }
          console.log("Failed to retrieve current SW to capture version hash");
          throw new Error(response.status + response.statusText);
        })
        .then(swContents => {
          // Extract and set versionId
          const extractedHash = extractSwVersionHash(swContents);
          // console.info("Extracted SW version hash as: ", extractedHash);
          return resolve(extractedHash);
        })
        .catch(error => {
          console.error(
            "Failed to fetch Service Worker for polled version checking",
            error
          );
          return resolve(undefined);
        });
    });
  }

  /**
   * Retrieves the latest version of the service-worker.js file
   * and uses individual file hashes to check if it differs from the currently running version.
   */
  private checkForUpdates() {
    console.log("Checking for updates");
    const { currentVersionHash } = this.state;

    const r = new XMLHttpRequest();
    const dateStr = new Date().getTime();
    r.onload = () => {
      const t = r.responseText;
      const incomingSwHash = extractSwVersionHash(t);
      // TODO: Check for undefined?
      if (incomingSwHash !== currentVersionHash) {
        console.log(
          "Update found through polling check. Stopping polling. CurrentHash, IncomingHash: ",
          currentVersionHash,
          incomingSwHash
        );
        clearInterval(this.state.updateCheckTimerId);
        this.setState({ updateAvailable: true });
      }
    };
    r.open("GET", `/service-worker.js?c=${dateStr}`);
    r.setRequestHeader("Cache-Control", "no-cache, no-store, must-revalidate");
    r.send();
  }

  /**
   * Forces current active SW to check if a new version is available
   */
  private triggerSWUpdate() {
    console.log("Focing current SW to update.");
    if (navigator.serviceWorker) {
      navigator.serviceWorker.getRegistrations().then(regs => {
        if (regs[0]) {
          console.log("Found one active service worker. Forcing SW update.");
          regs[0].update();
        }
      });
    } else {
      console.log("No active service worker found.");
    }
  }

  /**
   * Invoked by the new service worker when it is installed.
   * We need to differentiate between SW updates forced by our code
   * vs updates found through the normal PWA lifecycle (Navigation events, app launch, etc.)
   * @param e
   */
  private handleUpdateFoundSWEvent(e) {
    console.log("handleUpdateFoundSWEvent event: ", e);
    if (e.detail && e.detail.sourceSW) {
      console.log("SourceSW", e.detail);
    }

    clearInterval(this.state.updateCheckTimerId);
    this.setState({
      updateAvailable: true,
      updateSourceSW: e.detail.sourceSW
    });
  }

  private loadNewAppVersion = () => {
    console.log("Loading new app version");

    // Force SW internal update
    this.triggerSWUpdate();

    const { updateSourceSW } = this.state;
    if (updateSourceSW) {
      console.log(
        "Found SW update source, sending SKIP_WAITING before restart"
      );
      updateSourceSW.postMessage({ type: "SKIP_WAITING" });

      window.setInterval(() => {
        window.location.reload(true);
      }, DELAY_BEFORE_MANUAL_RESTART);
    } else {
      console.log("No SW update source found. Restarting directly");
      window.setInterval(() => {
        window.location.reload(true);
      }, DELAY_BEFORE_MANUAL_RESTART);
    }
  };

  /************************
   * Idle detection logic
   */

  private handleUserInteraction = () => {
    const now = Date.now();
    // console.log("User interaction received at:", now);
    this.setState({ lastUserInteraction: now });
  };

  private checkUserIdleState = () => {
    const now = Date.now();
    const timeSinceLastInteraction = now - this.state.lastUserInteraction;

    if (timeSinceLastInteraction > AUTO_UPDATE_IDLE_DELAY) {
      // console.log(
      //   "The user has been idle for longer than the Idle Detection Delay: ",
      //   timeSinceLastInteraction,
      //   AUTO_UPDATE_IDLE_DELAY
      // );
      if (this.state.updateAvailable) {
        console.log(
          "Update available. Idle tablet detected. Starting auto-update"
        );
        const loadNewAppVersionTimerId = window.setInterval(() => {
          this.loadNewAppVersion();
        }, AUTO_UPDATE_NOTIFICATION_DELAY);

        this.setState({
          autoUpdateStarted: true,
          loadNewAppVersionTimerId
        });
      }
    }
  };
}

export default UpdateManager;



// WEBPACK FOOTER //
// ./src/components/UpdateManager/UpdateManager.tsx